<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed } from "vue"
import { useSourceStore } from "@core/stores/source.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useTaskStore } from "@core/stores/task.store"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import type { Source } from "@types"

import { useSourceInfo } from "@core/composables/useSourceInfo"

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Identifiant du capteur
     */
    sourceId: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Informations générales du capteur",
            buttons: {
                edit: "Modifier les informations",
            },
            breadcrumb: {
                sensor: "Informations générales du capteur",
            },
            empty: "Aucune source trouvée",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const sourceStore = useSourceStore()
const authStore = useAuthStore()
const taskStore = useTaskStore()

const { isLoadingModelsAndBrands } = useSourceInfo()

const { getSourceById } = storeToRefs(sourceStore)

// ================================
// STATE
// ================================
/**
 * Indique si le composant est en mode édition
 */
const isEditMode = ref<boolean>(false)

// ================================
// COMPUTED
// ================================
/**
 * Récupère la source depuis le store
 */
const getSource = computed<Source | undefined>(() => {
    return getSourceById.value(props.sourceId)
})

/**
 * Récupère le buildingId depuis la source (premier building)
 */
const getBuildingId = computed<string | undefined>(() => {
    return getSource.value?.buildings?.[0]?._id
})

/**
 * Vérifie si l'utilisateur a les permissions pour éditer
 */
const getCanEdit = computed<boolean>(() => {
    return authStore.hasOfferEnergyManager && authStore.isEmployeWriteOrHigher
})

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Crée une tâche de rebuild des données de la source
 * @param sourceId - Identifiant de la source
 */
async function createRebuildTask(sourceId: string): Promise<void> {
    try {
        await taskStore.launchTask(
            "source.rebuild_data.request",
            { _id_source: sourceId },
            undefined,
            {
                taskname: "Récupération des données du capteur",
                notification: true,
                autoDelete: true,
            }
        )
    } catch (error) {
        console.error(
            "Erreur lors de la création de la tâche de rebuild:",
            error
        )
    }
}

// ================================
// INTERACTIONS
// ================================
/**
 * Gère le clic sur le bouton "Modifier"
 */
function onClickEdit(): void {
    isEditMode.value = true
}

/**
 * Gère l'annulation de l'édition
 */
function onCancelEdit(): void {
    isEditMode.value = false
}

/**
 * Gère la fin de mise à jour du capteur
 * @param sourceId - Identifiant de la source mise à jour
 */
async function onUpdateDone(sourceId?: string): Promise<void> {
    isEditMode.value = false

    // Crée la tâche de rebuild
    if (sourceId) {
        await createRebuildTask(sourceId)
    }
}
</script>

<template>
    <div class="sensor-info-wrapper flex flex-col gap-16">
        <!-- ================================ -->
        <!-- EN-TÊTE -->
        <!-- ================================ -->
        <HeaderPageSource
            :title="t('title')"
            :source-id="sourceId"
            icon="pi pi-info-circle"
            class-header="flex items-center justify-between"
        >
            <BaseButton
                v-if="getCanEdit && !isEditMode"
                :label="t('buttons.edit')"
                icon="pi pi-pencil"
                icon-pos="right"
                :disabled="isLoadingModelsAndBrands"
                @click="onClickEdit"
            />
        </HeaderPageSource>

        <!-- ================================ -->
        <!-- MODE LECTURE -->
        <!-- ================================ -->
        <template v-if="!isEditMode && getSource">
            <!--
                DEV-83 spike — @edit-sensor : déclenché par le banner "capteur
                inexistant" si présent dans SensorInfoDisplay. Réutilise le même
                bascule en mode édition que le bouton "Modifier les informations"
                du header. 2 points d'entrée vers le même parcours XState.
            -->
            <SensorInfoDisplay
                :source-id="sourceId"
                @edit-sensor="onClickEdit"
            />
            <SensorPositionHistory :source-id="sourceId" />
        </template>

        <!-- ================================ -->
        <!-- MODE ÉDITION -->
        <!-- ================================ -->
        <SensorForm
            v-else-if="isEditMode && getSource"
            :source-id="sourceId"
            :building-id="getBuildingId"
            :disable-rebuild="true"
            @done="onUpdateDone"
            @cancel="onCancelEdit"
        />

        <!-- ================================ -->
        <!-- ÉTAT VIDE -->
        <!-- ================================ -->
        <p v-else>{{ t("empty") }}</p>
    </div>
</template>
